import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const impactAreas = [
    {
        title: 'Health Education',
        desc: 'Over 5,000 people reached through our health education campaigns.',
        status: '2023-2024',
        icon: (
            <svg className="h-10 w-10 text-lochmara-blue" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" /></svg>
        ),
    },
    {
        title: 'Women Empowerment',
        desc: 'Empowering women in rural areas with skills and support.',
        status: 'Ongoing',
        icon: (
            <svg className="h-10 w-10 text-malachite-green" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 8v8m-4-4h8" /></svg>
        ),
    },
    {
        title: 'Community Clinics',
        desc: 'Mobile clinics providing healthcare to underserved communities.',
        status: '2024',
        icon: (
            <svg className="h-10 w-10 text-brand" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M16 3v4M8 3v4" /></svg>
        ),
    },
];

const gallery = [
    {
        src: 'image/comm3.jpg',
        caption: 'Rural Health Outreach',
    },
    {
        src: 'image/comm4.jpg',
        caption: 'Women Empowerment Workshop',
    },
    {
        src: 'image/comm5.jpg',
        caption: 'Community Health Workshop', 
    },
    {
        src: 'image/comm1.jpg',
        caption: 'Community Health Education',
    },
    {
        src: 'image/comm.jpg',
        caption: 'School Health Program',
    },
    {
        src: 'image/comm2.jpg',
        caption: 'Nutrition Outreach',
    },
];

const Community = () => {
    const [zoomImg, setZoomImg] = useState<{ src: string; caption: string } | null>(null);

    // Close modal on ESC
    React.useEffect(() => {
        if (!zoomImg) return;
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoomImg(null); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [zoomImg]);

    return (
        <div className="flex min-h-screen w-full flex-col bg-gradient-to-br from-blue-50 to-teal-100">
            {/* Hero Section */}
            <section className="from-brand-light animate-fade-in relative flex w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br to-blue-100 py-16 md:py-24">
                <div className="animate-fade-in relative z-10 mx-auto max-w-3xl text-center delay-100">
                    <h1 className="animate-fade-in mb-4 font-ubuntu text-4xl font-bold text-lochmara-blue drop-shadow-lg delay-200 md:text-5xl">Community Impact</h1>
                    <p className="animate-fade-in mb-6 font-redhat text-xl text-gray-700 delay-300 md:text-2xl">Transforming lives through education, outreach, and empowerment in the communities we serve.</p>
                </div>
                {/* Animated Bubbles and Cross Motif */}
                <svg className="animate-float-slow absolute left-10 top-10 z-0 h-24 w-24 opacity-30" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="40" fill="#0087ca" /></svg>
                <svg className="animate-float-medium absolute right-20 top-24 z-0 h-16 w-16 opacity-20" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="35" fill="#00b937" /></svg>
                <svg className="animate-float-fast absolute bottom-10 left-1/2 z-0 h-20 w-20 opacity-20" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="30" fill="#004487" /></svg>
                <svg className="animate-float-cross absolute bottom-16 right-10 z-0 h-16 w-16" viewBox="0 0 64 64" fill="none"><rect x="28" y="8" width="8" height="48" rx="4" fill="#0087ca" /><rect x="8" y="28" width="48" height="8" rx="4" fill="#00b937" /></svg>
                <style>{`
        @keyframes fade-in { 0% { opacity: 0; transform: translateY(40px);} 100% { opacity: 1; transform: translateY(0);} }
        .animate-fade-in { animation: fade-in 1.2s cubic-bezier(.4,0,.2,1) both; }
        .delay-200 { animation-delay: 0.2s; }
        @keyframes float-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
        .animate-float-slow { animation: float-slow 7s ease-in-out infinite; }
        @keyframes float-medium { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-30px); } }
        .animate-float-medium { animation: float-medium 5s ease-in-out infinite; }
        @keyframes float-fast { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-40px); } }
        .animate-float-fast { animation: float-fast 3.5s ease-in-out infinite; }
        @keyframes float-cross { 0%, 100% { transform: translateY(0) rotate(-8deg); } 50% { transform: translateY(-18px) rotate(8deg); } }
        .animate-float-cross { animation: float-cross 6s ease-in-out infinite; }
      `}</style>
            </section>

            {/* Impact Cards */}
            <section className="w-full px-4 py-12">
                <div className="mx-auto grid max-w-5xl grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
                    {impactAreas.map((area, idx) => (
                        <div key={area.title} className={`animate-fade-in group flex cursor-pointer flex-col items-center rounded-2xl bg-white/80 p-8 text-center shadow-lg transition-transform duration-300 hover:-translate-y-2 hover:shadow-2xl`} style={{ animationDelay: `${0.3 + idx * 0.15}s` }}>
                            <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white shadow transition-all duration-300 group-hover:bg-blue-50">
                                {area.icon}
                            </span>
                            <h2 className="mb-2 text-xl font-bold text-lochmara-blue">{area.title}</h2>
                            <p className="mb-4 font-redhat text-gray-700">{area.desc}</p>
                            <span className="mb-2 inline-block w-fit rounded-full bg-lochmara-blue/10 px-4 py-1 text-xs font-bold text-lochmara-blue">{area.status}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Community Services Gallery */}
            <section className="w-full bg-white/80 px-4 py-12">
                <div className="mx-auto max-w-6xl">
                    <h2 className="animate-fade-in mb-8 text-center font-ubuntu text-2xl font-bold text-congress-blue delay-100 md:text-3xl">Community Services Gallery</h2>
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                        {gallery.map((img, idx) => (
                            <div key={img.caption} className="animate-fade-in flex cursor-zoom-in flex-col items-center overflow-hidden rounded-xl bg-white shadow-lg" style={{ animationDelay: `${0.4 + idx * 0.1}s` }} onClick={() => setZoomImg(img)}>
                                <img src={img.src} alt={img.caption} className="h-64 w-full object-cover" />
                                <div className="p-3 text-center font-redhat text-sm text-gray-700">{img.caption}</div>
                            </div>
                        ))}
                    </div>
                </div>
                {/* Modal/Lightbox */}
                {zoomImg && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={() => setZoomImg(null)}>
                        <div className="relative mx-4 w-full max-w-2xl" onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
                            <button className="absolute right-2 top-2 z-10 text-3xl font-bold text-white transition hover:text-brand" onClick={() => setZoomImg(null)} aria-label="Close">&times;</button>
                            <img src={zoomImg.src} alt={zoomImg.caption} className="max-h-[80vh] w-full rounded-xl object-contain shadow-lg" />
                            <div className="mt-4 text-center font-redhat text-lg text-white">{zoomImg.caption}</div>
                        </div>
                    </div>
                )}
            </section>

            {/* Call to Action Section */}
            <section className="flex w-full flex-col items-center bg-gradient-to-br from-blue-100 to-teal-50 px-4 py-12">
                <h2 className="mb-4 text-center font-ubuntu text-2xl font-bold text-congress-blue md:text-3xl">Want to Make a Difference?</h2>
                <p className="mb-6 max-w-2xl text-center font-redhat text-lg text-gray-700">Join us in our mission to empower communities. Contact us to volunteer, partner, or support our initiatives.</p>
                <Link to="/contact" className="rounded-full bg-brand px-8 py-4 font-ubuntu text-lg font-semibold text-white shadow-lg transition-transform duration-200 hover:scale-105 hover:bg-brand-green">Contact Us</Link>
            </section>
        </div>
    );
};

export default Community;
